<template>
    <el-dropdown
        ref="dropdownRef"
        popper-class="custom-dropdown-new dropdown-megamenu"
        :show-arrow="false"
        @visible-change="(v: boolean) => { dropdownOpen = v; $emit('visibleChange', v) }"
    >
        <span class="text-16 inline-flex items-center gap-4px">
            {{ t('common.developerCenter') }}
            <icon-svg-arrow-down class="w-12 h-12 transition-transform duration-200" :class="{ 'rotate-180': dropdownOpen }" />
        </span>
        <template #dropdown>
            <div class="mm-panel">
                <!-- 两栏：文档与指南（单列磁贴）/ 学习与培训（竖向列表）
                     （原型 .mega-body.simple-columns.developer-columns） -->
                <div class="mm-body is-doc">
                    <section v-for="(group, gIdx) in dropdownList" :key="gIdx" class="mm-col">
                        <p class="mm-col-title">{{ label(group.name) }}</p>

                        <div v-if="group.layout === 'tiles'" class="mm-tile-grid">
                            <template v-for="(item, iIdx) in itemsOf(group)" :key="iIdx">
                                <!-- 芯片适配指南：磁贴内再挂两条适配路径（原型 .menu-tile.guide-tile） -->
                                <div v-if="item.variant === 'guide'" class="mm-tile mm-guide-tile">
                                    <span>
                                        <b class="text-#262642">{{ label(item.name) }}</b>
                                        <span v-if="item.desc">{{ label(item.desc) }}</span>
                                    </span>
                                    <div class="mm-guide-entries">
                                        <a v-for="(entry, eIdx) in itemsOf(item)" :key="eIdx" class="mm-guide-entry" @click="handleEntryClick(entry)">
                                            <span>
                                                <b class="text-#262642">{{ label(entry.name) }}</b>
                                                <small v-if="entry.desc">{{ label(entry.desc) }}</small>
                                            </span>
                                        </a>
                                    </div>
                                </div>
                                <div v-else class="mm-tile" @click="handleEntryClick(item)">
                                    <span>
                                        <b class="text-#262642">
                                            {{ label(item.name) }}
                                            <icon-svg-new-tag v-if="item.badge" class="mm-badge" :aria-label="item.badge" />
                                        </b>
                                        <span v-if="item.desc">{{ label(item.desc) }}</span>
                                    </span>
                                </div>
                            </template>
                        </div>

                        <div v-else class="mm-list">
                            <div v-for="(item, iIdx) in itemsOf(group)" :key="iIdx" class="mm-card" @click="handleEntryClick(item)">
                                <b class="mm-card-name">
                                    {{ label(item.name) }}
                                    <icon-svg-new-tag v-if="item.badge" class="mm-badge" :aria-label="item.badge" />
                                </b>
                                <span v-if="item.desc" class="mm-card-desc">{{ label(item.desc) }}</span>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </template>
    </el-dropdown>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { menuList, type DropdownItem } from '@/layout/menuData';

const emit = defineEmits<{
    (e: 'visibleChange', visible: boolean): void;
    (e: 'jumpPage', child: DropdownItem): void;
}>();

const { t } = useI18n();

const dropdownOpen = ref(false);

const dropdownList = menuList.find(m => m.name === 'common.developerCenter')!.dropdownList!;

/**
 * 安全取文案：key 为空/undefined 时返回空串
 * 分类节点没有 desc，直接 t(undefined) 会抛错并导致整页渲染失败
 */
const label = (key?: string) => (key ? t(key) : '');

/** 统一成「条目数组」：分类取其子项，普通条目即自身 */
const itemsOf = (item: DropdownItem): DropdownItem[] => item.list || [];

const dropdownRef = ref();

/** 点击条目：先收起弹层再触发跳转（面板为自定义结构，不会自动关闭） */
const handleEntryClick = (item: DropdownItem) => {
    dropdownRef.value?.handleClose?.();
    emit('jumpPage', item);
};
</script>

<style lang="scss">
/*
 * 开发者中心下拉：样式取自设计原型「FlagOS 2.2 官网导航及首页优化」
 * 三个下拉共用同一弹层类 .dropdown-megamenu，共享基元
 * （--mm-* 设计变量 / .mm-panel / .mm-body.is-doc / .mm-col-title / .mm-tile-grid /
 *  .mm-tile / .mm-list / .mm-card / .mm-badge）定义在 MenuProjects.vue，这里只补充专有部分：
 *   .mm-guide-tile    ← .menu-tile.guide-tile
 *   .mm-guide-entries ← .guide-entry-list
 *   .mm-guide-entry   ← .guide-entry
 */
.dropdown-megamenu {
    /* 原型 .developer-columns .tile-grid：文档与指南为单列磁贴、间距更紧 */
    .mm-body.is-doc .mm-tile-grid {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    /* 原型 .menu-tile.guide-tile：整行铺满的更高磁贴，本身不可点击 */
    .mm-guide-tile {
        grid-column: 1 / -1;
        min-height: 92px;
        cursor: default;

        > span > b::after {
            display: none;
        }

        &:hover {
            transform: none;
        }
    }

    /* 原型 .guide-entry-list */
    .mm-guide-entries {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6px;
        margin-top: 10px;
    }

    /* 原型 .guide-entry */
    .mm-guide-entry {
        position: relative;
        min-height: 56px;
        padding: 10px 28px 10px 12px;
        display: flex;
        align-items: center;
        border-radius: 10px;
        color: #3d465e;
        background: transparent;
        cursor: pointer;
        transition: 0.18s ease;

        &:hover {
            color: var(--mm-brand);
            background: var(--mm-soft-blue);
        }

        b::after {
            content: '→';
            position: absolute;
            right: 12px;
            font-size: 12px;
            line-height: normal;
            opacity: 0;
            transform: translateX(-3px);
            transition: 0.18s ease;
        }

        &:hover b::after {
            opacity: 1;
            transform: translateX(0);
        }
    }

    /* 原型 .guide-entry b / .guide-entry small：
       原型与 .menu-tile span 同为 (0,1,1) 权重、靠书写顺序覆盖，
       这里跨文件，多用一层父选择器保证权重胜出 */
    .mm-guide-tile .mm-guide-entry b {
        display: block;
        margin-bottom: 2px;
        font-size: 12px;
    }

    .mm-guide-tile .mm-guide-entry small {
        display: block;
        font-size: 10px;
        line-height: 1.35;
    }
}
</style>
